import type { Brand } from '@/types/constants';
import { getConfig } from '@/utils/config/config';
import { COOKIES, getCookieValue } from '@/utils/cookies';
import { ddRumAction } from '@/utils/datadog';
import { buildApiUrl, buildUrl } from '@/utils/url/build-url';
import { runWithRetry, type RetryExhaustionInfo } from './fetcher/retry';

enum LoyaltyRewardsApiModules {
    REWARDS_MACHINE = 'rewards_machine'
}

const MODULES_PROPERTY = 'modules[]';

const getHeaders = async () => {
    const region = await getCookieValue(COOKIES.REGION);
    const brand = process.env.BRAND as Brand;

    return {
        'x-region-code': region,
        'x-brand': brand
    };
};

const getLoyaltyRewardsApiBaseUrl = () => {
    const config = getConfig();

    return buildApiUrl(config.apis.LoyaltyRewardsClient);
};

const emitExhaustedRum = (info: RetryExhaustionInfo) => {
    ddRumAction({
        name: 'GENEROSITY_RETRY_EXHAUSTED',
        description: `loyalty-rewards-components exhausted after ${info.attempts} attempts (${info.reason})`,
        use_case: 'GENEROSITY_RETRY',
        context: {
            endpoint: 'reward-machine-components',
            attempts: info.attempts,
            reason: info.reason,
            status: info.status
        }
    });
};

export const loyaltyRewardsComponents = async (modules: string[], params?: Record<string, string>) => {
    if (!modules.length) {
        return;
    }

    const baseUrl = getLoyaltyRewardsApiBaseUrl();
    const headers = await getHeaders();
    const modulesParam = modules.join(`&${MODULES_PROPERTY}=`);

    const url = buildUrl(`${baseUrl}/components`, {
        product: process.env.PRODUCT as string,
        [MODULES_PROPERTY]: modulesParam,
        ...params
    });

    const json = await runWithRetry<Record<string, any>>({
        url,
        init: { method: 'GET', headers },
        networkConfig: getConfig().network?.InternalLoyaltyRewardsClient,
        parse: (res) => res.json(),
        onExhausted: emitExhaustedRum
    });

    if (json === null) {
        throw new Error('loyalty-rewards-components: exhausted retries');
    }

    return json;
};

export const getRewardMachineContent = async () => {
    return loyaltyRewardsComponents([LoyaltyRewardsApiModules.REWARDS_MACHINE]);
};
